import { useState, useCallback, useEffect, useMemo } from "react";
import { Upload, FileText } from "lucide-react";
import type { ContractUploadDetails } from "../../types/contractUploadDetails";
import type { BusinessUnitOption } from "../../types/businessUnit";
import { validateFile } from "../../services/uploadService";
import { DEFAULT_BUSINESS_UNIT_NAME } from "../../constants/defaultBusinessUnit";
import { ALL_BUSINESS_UNITS_SENTINEL } from "../../utils/businessUnitApiCodes";

/** Human-friendly default contract title: file basename without extension. */
export const displayNameFromFileName = (fileName: string): string => {
  const n = fileName.trim();
  if (!n) return "";
  const base = n.split(/[/\\]/).pop() ?? n;
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return base;
  return base.slice(0, dot);
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

type UploadAndReviewViewProps = {
  /** Active BUs from GET /business-units (merged with synthetic “All BUs” row when missing). */
  businessUnitOptions: readonly BusinessUnitOption[];
  onInitiateReview: (details: ContractUploadDetails) => void | Promise<void>;
  submitError?: string | null;
  isSubmitting?: boolean;
};

const ORIGINATIONS = ["External", "Internal"];
const CONTRACT_TYPES = ["NDA", "MSA", "SOW"];

export const UploadAndReviewView = ({
  businessUnitOptions,
  onInitiateReview,
  submitError = null,
  isSubmitting = false,
}: UploadAndReviewViewProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [contractName, setContractName] = useState("");
  const [contractType, setContractType] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [contractOrigination, setContractOrigination] = useState("");
  const selectableBus = useMemo(
    () =>
      businessUnitOptions.filter(
        (o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code,
      ),
    [businessUnitOptions],
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [contractNameError, setContractNameError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setBusinessUnit((current) => {
      if (current !== "") {
        return current;
      }
      const match = selectableBus.find(
        (o) => o.name === DEFAULT_BUSINESS_UNIT_NAME,
      );
      return match?.code ?? "";
    });
  }, [selectableBus]);

  const handleFileChange = useCallback((selectedFile: File | null) => {
    setUploadError(null);
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const result = validateFile(selectedFile);
    if (!result.valid) {
      setUploadError(result.error);
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setContractName(displayNameFromFileName(selectedFile.name));
  }, []);

  const handleBrowseChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      handleFileChange(f ?? null);
      e.target.value = "";
    },
    [handleFileChange],
  );

  const handleContinueToDetails = useCallback(() => {
    if (!file) return;
    if (!contractName.trim()) {
      setContractName(displayNameFromFileName(file.name));
    }
    setStep(2);
  }, [file, contractName]);

  const isFormValid = Boolean(
    contractName.trim() && contractOrigination && contractType && businessUnit,
  );

  const handleInitiateReview = useCallback(() => {
    setFormError(null);
    setContractNameError(null);

    const trimmed = contractName.trim();
    if (!trimmed) {
      setContractNameError("Contract Name is required");
      return;
    }

    if (!file) return;

    const details: ContractUploadDetails = {
      file,
      contractName: trimmed,
      contractType,
      businessUnit,
      contractOrigination,
    };
    void onInitiateReview(details);
  }, [
    contractName,
    contractType,
    businessUnit,
    contractOrigination,
    file,
    onInitiateReview,
  ]);

  if (step === 1) {
    return (
      <div className="space-y-4">
        {!file ? (
          <div
            role="region"
            aria-label="File upload drop zone"
            className="rounded-2xl border-2 border-dashed border-[var(--border)] bg-card p-6 text-center transition-colors hover:border-primary/50"
          >
            <input
              id="file-upload"
              type="file"
              accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleBrowseChange}
              className="hidden"
            />
            <div className="space-y-3">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <Upload className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Upload Contract Document
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click to upload your document.
                </p>
              </div>
              <button
                type="button"
                onClick={() => document.getElementById("file-upload")?.click()}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Browse Files
              </button>
              <p className="text-xs text-muted-foreground">
                Supported formats: DOCX. Max 25 MB.
              </p>
            </div>
          </div>
        ) : (
          <div
            role="region"
            aria-label="File uploaded success"
            className="rounded-2xl border-2 border-dashed border-green-400 bg-green-50/50 p-6 text-center"
          >
            <div className="space-y-3">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
                <FileText className="h-8 w-8" />
              </div>
              <h3 className="font-semibold text-gray-900">File Uploaded</h3>
              <div className="rounded-lg border border-green-300 bg-white px-4 py-3 text-left">
                <div className="flex items-start gap-3">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {uploadError && (
          <p className="text-sm text-destructive" role="alert">
            {uploadError}
          </p>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-blue-900 mb-2">
            Upload Guidelines
          </p>
          <ul className="mt-1 list-inside list-disc text-xs text-blue-800 space-y-1">
            <li>Maximum file size: 25 MB</li>
            <li>Use a complete, readable document</li>
            <li>File will be analyzed for review</li>
          </ul>
        </div>

        <button
          type="button"
          onClick={handleContinueToDetails}
          disabled={!file}
          className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          Continue to Review Details
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground">
          Contract Review Details
        </h2>
        <p className="text-sm text-muted-foreground">
          {file ? `Uploaded: ${file.name}` : "No file selected"}
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label
            htmlFor="contract-name"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Contract Name <span className="text-destructive">*</span>
          </label>
          <input
            id="contract-name"
            type="text"
            value={contractName}
            onChange={(e) => {
              setContractName(e.target.value);
              if (contractNameError) setContractNameError(null);
            }}
            placeholder="Defaults to file name; edit if needed"
            className={`w-full rounded-lg border bg-input-background px-3 py-2 text-sm ${
              contractNameError
                ? "border-destructive"
                : "border-[var(--border)]"
            }`}
            aria-required
            aria-invalid={!!contractNameError}
            aria-describedby={
              contractNameError ? "contract-name-error" : undefined
            }
          />
          {contractNameError && (
            <p
              id="contract-name-error"
              className="mt-1 text-sm text-destructive"
              role="alert"
            >
              {contractNameError}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="contract-type"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Contract Type <span className="text-destructive">*</span>
          </label>
          <select
            id="contract-type"
            value={contractType}
            onChange={(e) => setContractType(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select type</option>
            {CONTRACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="business-unit"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Business Unit <span className="text-destructive">*</span>
          </label>
          <select
            id="business-unit"
            value={businessUnit}
            onChange={(e) => setBusinessUnit(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select unit</option>
            {businessUnitOptions.some(
              (o) => o.code === ALL_BUSINESS_UNITS_SENTINEL.code,
            ) ? (
              <option value={ALL_BUSINESS_UNITS_SENTINEL.code}>
                {ALL_BUSINESS_UNITS_SENTINEL.name}
              </option>
            ) : null}
            {selectableBus.map((o) => (
              <option key={o.code} value={o.code}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="contract-origination"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Contract Origination <span className="text-destructive">*</span>
          </label>
          <select
            id="contract-origination"
            value={contractOrigination}
            onChange={(e) => setContractOrigination(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select origination</option>
            {ORIGINATIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      {formError && (
        <p className="text-sm text-destructive" role="alert">
          {formError}
        </p>
      )}

      {submitError && (
        <p className="text-sm text-destructive" role="alert">
          {submitError}
        </p>
      )}

      <div className="flex gap-2">
        {/* <button
          type="button"
          onClick={() => setStep(1)}
          className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Back
        </button> */}
        <button
          type="button"
          onClick={handleInitiateReview}
          disabled={!isFormValid || isSubmitting}
          className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isSubmitting ? "Submitting…" : "Initiate Review"}
        </button>
      </div>
    </div>
  );
};
